/**
 * Admin → Auto-resolve rules: mail matching a rule is resolved as soon as it
 * is classified and never enters the sales / support queue (receipts,
 * newsletters, autoreplies…).
 *
 * The editor previews, while the rule is typed, which open mails it would
 * catch now (POST auto-resolve-rules/preview), and can resolve those too once
 * the rule is saved (POST auto-resolve-rules/{secId}/apply). Backend:
 * SalesInboxAutoResolveRuleController, matching in AutoResolveRules.
 */
define('salesInboxAutoResolveRulesView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'entities/sales_inbox'
], function (Module, App, _, $, Marionette) {
  'use strict';

  var CATEGORIES = ['', 'SALES', 'DESIGN', 'INVOICING', 'RECLAMATION', 'TRACKING', 'NOT_ACTIONABLE',
                    'SUPPLIER_ORDER', 'SUPPLIER_SHIPPING', 'SUPPLIER_PROOF', 'SUPPLIER_INVOICE', 'SUPPLIER_CLAIM'];
  var INBOXES = ['', 'SALES', 'SUPPORT'];
  var CASES_BY_CATEGORY = {
    SALES:          ['PRICE_NEGOTIATION', 'OFFER_DECLINED', 'EXPRESS_DELIVERY', 'STOCKS', 'PURCHASE_ORDER', 'REORDER', 'READY_TO_ORDER', 'DELIVERY_TIME', 'SAMPLE', 'QUANTITY', 'OFFER_REQUEST', 'CHANGE', 'UNFINISHED_ORDER', 'UNDER_REVIEW'],
    DESIGN:         ['ASK_FOR_MODIFICATION', 'LOGO_NOT_PRINTABLE', 'NO_LOGO_SENT'],
    INVOICING:      ['ASK_FOR_INVOICE', 'PAY_AFTER_DELIVERY', 'CHANGE_DATA', 'ASK_FOR_RIB', 'PAYMENT_RECEIVED'],
    RECLAMATION:    ['PRINTING_ISSUE', 'BROKEN_GOODS', 'MISSING_GOODS'],
    TRACKING:       ['WHEN_WILL_ARRIVE', 'DID_NOT_ARRIVE', 'PROBLEM'],
    NOT_ACTIONABLE: ['OUT_OF_OFFICE', 'EMAIL_NOT_DELIVERED', 'CARRIER_SURVEY', 'VENDOR_SOLICITATION'],
    SUPPLIER_ORDER:    ['ORDER_CONFIRMED', 'ORDER_DELAYED', 'OUT_OF_STOCK', 'SUPPLIER_QUESTION'],
    SUPPLIER_SHIPPING: ['SHIPPED', 'SHIPPING_ISSUE'],
    SUPPLIER_PROOF:    ['PROOF_TO_VALIDATE', 'ARTWORK_ISSUE'],
    SUPPLIER_INVOICE:  ['INVOICE_RECEIVED', 'PAYMENT_REMINDER', 'CREDIT_NOTE'],
    SUPPLIER_CLAIM:    ['CLAIM_UPDATE']
  };

  function pretty(code) {
    if (!code) { return ''; }
    var s = String(code).toLowerCase().replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function esc(s) { return _.escape(s === null || s === undefined ? '' : String(s)); }

  // Subject words travel "|"-separated; the editor shows one per line.
  function wordsToLines(s) { return (s || '').split('|').map($.trim).filter(Boolean).join('\n'); }
  function linesToWords(s) { return (s || '').split(/\n|\|/).map($.trim).filter(Boolean).join(' | '); }

  function hasCondition(p) {
    return !!(p.category || p.emailCase || $.trim(p.senders || '').replace(/[,;\s]/g, '') ||
      $.trim(p.subjectContains || '').replace(/[|\s]/g, ''));
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    function pad(n) { return n < 10 ? '0' + n : n; }
    return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear() + ' ' +
      pad(d.getHours()) + ':' + pad(d.getMinutes());
  }

  function conditionsHtml(rule) {
    var parts = [];
    if (rule.inboxType) { parts.push('<span class="ar-chip">' + esc(pretty(rule.inboxType)) + ' inbox</span>'); }
    if (rule.category) { parts.push('<span class="ar-chip">' + esc(pretty(rule.category)) + '</span>'); }
    if (rule.emailCase) { parts.push('<span class="ar-chip">' + esc(pretty(rule.emailCase)) + '</span>'); }
    if (rule.senders) { parts.push('<span class="ar-cond"><b>From</b> ' + esc(rule.senders) + '</span>'); }
    if (rule.subjectContains) { parts.push('<span class="ar-cond"><b>Subject</b> ' + esc(rule.subjectContains) + '</span>'); }
    if (rule.minConfidence) { parts.push('<span class="ar-chip">≥ ' + Math.round(rule.minConfidence * 100) + '% sure</span>'); }
    if (rule.unknownSenderOnly) { parts.push('<span class="ar-chip">not from customers</span>'); }
    return parts.join(' ');
  }

  Module.exports = Marionette.ItemView.extend({
    className: 'sales-inbox-auto-resolve-view',
    template: _.template(
      '<div class="ar-toolbar">' +
      '  <div>' +
      '    <h3>Auto-resolve rules</h3>' +
      '    <p class="ar-intro">A mail matching an enabled rule is resolved as soon as it is classified and never ' +
      '      enters the sales or support queue. Every condition set must hold; one of the senders or one of the ' +
      '      subject words is enough. Only new mail is concerned — use “Resolve matching open mails” for the rest.</p>' +
      '  </div>' +
      '  <button type="button" class="ar-btn primary js-add">+ Add rule</button>' +
      '</div>' +
      '<table class="ar-table">' +
      '  <thead><tr><th>Name</th><th>Conditions</th><th>Resolved</th><th>Enabled</th><th></th></tr></thead>' +
      '  <tbody><tr><td colspan="5" class="ar-empty">Loading…</td></tr></tbody>' +
      '</table>'
    ),

    events: {
      'click .js-add': 'onAdd',
      'click .js-edit': 'onEdit',
      'click .js-delete': 'onDelete',
      'click .js-apply': 'onApply',
      'change .js-enabled': 'onToggle'
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      App.request('salesInbox:auto-resolve-rules:list').done(function (rules) {
        if (self.isDestroyed) { return; }
        self.rules = rules || [];
        self.renderRows();
      });
    },

    ruleOf: function (e) {
      return _.findWhere(this.rules, { secId: $(e.currentTarget).closest('tr').attr('data-secid') });
    },

    renderRows: function () {
      var $tbody = this.$('tbody').empty();
      if (!this.rules.length) {
        $tbody.html('<tr><td colspan="5" class="ar-empty">No rule yet. Typical ones: payment receipts ' +
          '(sender @mollie.com), newsletters, out-of-office replies (category Not actionable).</td></tr>');
        return;
      }
      _.each(this.rules, function (rule) {
        $tbody.append(
          '<tr data-secid="' + esc(rule.secId) + '" class="' + (rule.enabled ? '' : 'off') + '">' +
          '<td class="ar-name">' + esc(rule.name) + '</td>' +
          '<td class="ar-conds">' + conditionsHtml(rule) + '</td>' +
          '<td class="ar-hits"><b>' + (rule.matchCount || 0) + '</b>' +
          (rule.lastMatchedAt ? '<span>last ' + esc(formatDate(rule.lastMatchedAt)) + '</span>' : '') + '</td>' +
          '<td><input type="checkbox" class="js-enabled"' + (rule.enabled ? ' checked' : '') + '></td>' +
          '<td class="ar-actions">' +
          '  <button type="button" class="ar-btn js-apply" title="Resolve the open mails this rule matches now">Resolve matching open mails</button>' +
          '  <button type="button" class="ar-btn js-edit">Edit</button>' +
          '  <button type="button" class="ar-btn danger js-delete">Delete</button>' +
          '</td></tr>');
      });
    },

    onAdd: function () { this.openEditor(null); },

    onEdit: function (e) { this.openEditor(this.ruleOf(e)); },

    onDelete: function (e) {
      var self = this;
      var rule = this.ruleOf(e);
      if (!rule || !window.confirm('Delete the rule “' + rule.name + '”?')) { return; }
      App.request('salesInbox:auto-resolve-rules:delete', rule.secId).done(function () { self.load(); });
    },

    onToggle: function (e) {
      var self = this;
      var rule = this.ruleOf(e);
      if (!rule) { return; }
      var payload = _.extend({}, rule, { enabled: $(e.currentTarget).is(':checked') });
      App.request('salesInbox:auto-resolve-rules:save', rule.secId, payload).done(function (saved) {
        _.extend(rule, saved);
        self.renderRows();
      }).fail(function () { self.load(); });
    },

    onApply: function (e) {
      var self = this;
      var rule = this.ruleOf(e);
      if (!rule) { return; }
      var $btn = $(e.currentTarget).prop('disabled', true);
      App.request('salesInbox:auto-resolve-rules:preview', rule).done(function (preview) {
        var n = (preview && preview.count) || 0;
        if (!n) {
          window.alert('No open mail matches “' + rule.name + '” right now.');
          $btn.prop('disabled', false);
          return;
        }
        if (!window.confirm('Resolve the ' + n + ' open mail' + (n > 1 ? 's' : '') + ' matching “' + rule.name + '”?')) {
          $btn.prop('disabled', false);
          return;
        }
        self.applyRule(rule.secId).always(function () { $btn.prop('disabled', false); });
      }).fail(function () { $btn.prop('disabled', false); });
    },

    applyRule: function (secId) {
      var self = this;
      return App.request('salesInbox:auto-resolve-rules:apply', secId).done(function (resp) {
        var n = (resp && resp.resolved) || 0;
        window.alert(n + ' mail' + (n > 1 ? 's' : '') + ' resolved.');
        self.load();
      });
    },

    // ---- editor ---------------------------------------------------------
    openEditor: function (rule) {
      var self = this;
      var isNew = !rule;
      rule = rule || { enabled: true };

      var $overlay = $('<div class="ar-editor-overlay"></div>');
      var $panel = $('<div class="ar-editor"></div>').appendTo($overlay);
      $panel.append('<h4>' + (isNew ? 'New' : 'Edit') + ' auto-resolve rule</h4>');

      var $name = $('<input type="text" maxlength="128" placeholder="e.g. Mollie payment receipts">').val(rule.name || '');
      var $inbox = $('<select></select>');
      _.each(INBOXES, function (v) {
        $('<option></option>').val(v).text(v ? pretty(v) : '(both)')
          .prop('selected', (rule.inboxType || '') === v).appendTo($inbox);
      });
      var $category = $('<select></select>');
      _.each(CATEGORIES, function (v) {
        $('<option></option>').val(v).text(v ? pretty(v) : '(any)')
          .prop('selected', (rule.category || '') === v).appendTo($category);
      });
      var $case = $('<select></select>');
      function refreshCases() {
        var current = $case.val() || rule.emailCase || '';
        $case.empty().append('<option value="">(any)</option>');
        _.each(CASES_BY_CATEGORY[$category.val()] || [], function (c) {
          $('<option></option>').val(c).text(pretty(c)).prop('selected', current === c).appendTo($case);
        });
      }
      refreshCases();
      $category.on('change', refreshCases);
      var $senders = $('<textarea rows="3" placeholder="noreply@mollie.com&#10;@newsletter-domain.com"></textarea>')
        .val(rule.senders || '');
      var $subject = $('<textarea rows="3" placeholder="One word or phrase per line"></textarea>')
        .val(wordsToLines(rule.subjectContains));
      var $confidence = $('<input type="number" min="0" max="100" step="5" placeholder="any">')
        .val(rule.minConfidence ? Math.round(rule.minConfidence * 100) : '');
      var $unknown = $('<input type="checkbox">').prop('checked', !!rule.unknownSenderOnly);
      var $enabled = $('<input type="checkbox">').prop('checked', rule.enabled !== false);

      function row(label, $field, help) {
        var $r = $('<div class="ar-row"></div>');
        $r.append($('<label></label>').text(label));
        var $c = $('<div class="ar-field"></div>').append($field);
        if (help) { $c.append('<div class="ar-help">' + help + '</div>'); }
        return $r.append($c);
      }
      $panel.append(row('Name', $name));
      $panel.append(row('Inbox', $inbox));
      $panel.append(row('Category', $category));
      $panel.append(row('Case', $case));
      $panel.append(row('Senders', $senders, 'Addresses or domains (@mollie.com, subdomains included). One is enough.'));
      $panel.append(row('Subject contains', $subject, 'Not case-sensitive. One is enough.'));
      $panel.append(row('Min. confidence %', $confidence, 'Of the classification, for a category / case rule.'));
      $panel.append(row('Not from customers', $unknown, 'Leave alone a mail from a known customer.'));
      $panel.append(row('Enabled', $enabled));

      var $preview = $('<div class="ar-preview"><span class="ar-muted">Set a condition to see which open mails it catches.</span></div>');
      $panel.append($preview);
      var $applyRow = $('<label class="ar-apply-now" hidden><input type="checkbox"> <span></span></label>');
      $panel.append($applyRow);

      var $actions = $('<div class="ar-editor-actions"></div>');
      var $save = $('<button type="button" class="ar-btn primary">Save</button>');
      var $cancel = $('<button type="button" class="ar-btn">Cancel</button>');
      $actions.append($cancel).append($save);
      $panel.append($actions);

      function payload() {
        var conf = parseInt($confidence.val(), 10);
        return {
          name: $.trim($name.val()),
          inboxType: $inbox.val() || null,
          category: $category.val() || null,
          emailCase: $case.val() || null,
          senders: $.trim($senders.val()) || null,
          subjectContains: linesToWords($subject.val()) || null,
          minConfidence: isNaN(conf) ? null : Math.max(0, Math.min(100, conf)) / 100,
          unknownSenderOnly: $unknown.is(':checked'),
          enabled: $enabled.is(':checked')
        };
      }

      var previewReq = null;
      var refreshPreview = _.debounce(function () {
        var p = payload();
        if (!hasCondition(p)) {
          $preview.html('<span class="ar-muted">Set a condition to see which open mails it catches.</span>');
          $applyRow.prop('hidden', true);
          return;
        }
        $preview.addClass('loading');
        if (previewReq && previewReq.abort) { previewReq.abort(); }
        previewReq = App.request('salesInbox:auto-resolve-rules:preview', p);
        previewReq.done(function (res) {
          $preview.removeClass('loading');
          var n = (res && res.count) || 0;
          var html = '<div class="ar-preview-head"><b>' + n + '</b> open mail' + (n === 1 ? '' : 's') +
            ' would be resolved' + (res && res.truncated ? ' (among the 5000 newest)' : '') + '</div>';
          if (n) {
            html += '<ul>' + _.map(res.samples || [], function (s) {
              return '<li><span class="ar-from">' + esc(s.fromAddress) + '</span>' +
                '<span class="ar-subj">' + esc(s.subject || '(no subject)') + '</span>' +
                '<span class="ar-meta">' + esc(pretty(s.inboxType)) + (s.emailCase ? ' · ' + esc(pretty(s.emailCase)) : '') +
                ' · ' + esc(formatDate(s.sentDate)) + '</span></li>';
            }).join('') + '</ul>';
            if (n > (res.samples || []).length) { html += '<div class="ar-muted">…and ' + (n - res.samples.length) + ' more</div>'; }
          }
          $preview.html(html);
          $applyRow.prop('hidden', !n).find('span')
            .text('Also resolve these ' + n + ' open mail' + (n === 1 ? '' : 's') + ' now');
        }).fail(function (xhr) {
          if (xhr && xhr.statusText === 'abort') { return; }
          $preview.removeClass('loading').html('<span class="ar-muted">Preview unavailable.</span>');
        });
      }, 500);
      $panel.on('input change', 'input, select, textarea', function (e) {
        if ($(e.target).closest('.ar-apply-now').length) { return; }
        refreshPreview();
      });
      refreshPreview();

      $cancel.on('click', function () { $overlay.remove(); });
      $save.on('click', function () {
        var p = payload();
        if (!p.name) { window.alert('Give the rule a name.'); $name.focus(); return; }
        if (!hasCondition(p)) {
          window.alert('Set at least a category, a case, a sender or a subject word: a rule on the inbox alone would resolve everything.');
          return;
        }
        var applyNow = !$applyRow.prop('hidden') && $applyRow.find('input').is(':checked');
        $save.prop('disabled', true);
        App.request('salesInbox:auto-resolve-rules:save', isNew ? null : rule.secId, p).done(function (saved) {
          $overlay.remove();
          if (applyNow && saved && saved.secId) {
            self.applyRule(saved.secId);
          } else {
            self.load();
          }
        }).fail(function (xhr) {
          $save.prop('disabled', false);
          window.alert('Save failed' + (xhr && xhr.status === 400 ? ': a name and at least one condition are needed.' : '.'));
        });
      });

      $('body').append($overlay);
      $name.focus();
    }
  });
});

